import { useMemo } from "react";

const COLORS = ["#2B6BE0", "#5B37D6", "#8A2BC0", "#C4249A", "#E01E8C", "#FF2D7E", "#FF5A3C", "#FF7A18", "#FFB020"];
const rand = (a, b) => Math.random() * (b - a) + a;

// Original hand-built paint/powder smear (SVG). Directional brush with ragged edges,
// bristle texture and flicked-powder specks. Vibrant cosmos->sunset palette.
export const PaintSmear = ({ className = "", flip = false, style }) => {
  const uid = useMemo(() => "smear-" + Math.random().toString(36).slice(2, 7), []);
  const specks = useMemo(
    () =>
      Array.from({ length: 46 }).map(() => {
        const x = Math.random() < 0.5 ? rand(6, 150) : rand(270, 414);
        const y = rand(96, 236) + (Math.random() - 0.5) * 70;
        return {
          x,
          y,
          r: rand(1.1, 5.4),
          c: COLORS[Math.floor(rand(0, COLORS.length))],
          o: rand(0.3, 0.95),
        };
      }),
    []
  );

  const grad = `${uid}-g`;
  const rough = `${uid}-r`;
  const bristle = `${uid}-b`;
  const stroke =
    "M46 170 C130 150 240 146 330 154 C368 157 392 149 402 162 C384 184 356 190 326 185 C232 178 128 190 50 180 C38 178 34 174 46 170 Z";

  return (
    <svg
      className={`pointer-events-none select-none ${flip ? "scale-x-[-1]" : ""} ${className}`}
      style={style}
      viewBox="0 0 420 320"
      fill="none"
      aria-hidden
      data-testid="paint-smear"
    >
      <defs>
        <linearGradient id={grad} x1="0" y1="0.2" x2="1" y2="0.8">
          <stop offset="0%" stopColor="#2B6BE0" />
          <stop offset="20%" stopColor="#6A2FD0" />
          <stop offset="42%" stopColor="#B02397" />
          <stop offset="60%" stopColor="#FF2D7E" />
          <stop offset="80%" stopColor="#FF6A1A" />
          <stop offset="100%" stopColor="#FFB020" />
        </linearGradient>

        <filter id={rough} x="-25%" y="-60%" width="150%" height="220%">
          <feTurbulence type="fractalNoise" baseFrequency="0.016 0.13" numOctaves="2" seed="11" result="n" />
          <feDisplacementMap in="SourceGraphic" in2="n" scale="22" xChannelSelector="R" yChannelSelector="G" />
        </filter>

        <filter id={bristle} x="0%" y="0%" width="100%" height="100%">
          <feTurbulence type="fractalNoise" baseFrequency="0.7 0.02" numOctaves="3" seed="5" result="b" />
          <feColorMatrix in="b" type="matrix" values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.9 0" result="ba" />
          <feComposite in="ba" in2="SourceGraphic" operator="in" />
        </filter>
      </defs>

      <g transform="rotate(-19 210 160)">
        <g filter={`url(#${rough})`}>
          <path d={stroke} fill={`url(#${grad})`} />
          <path d={stroke} fill="#3a0a2a" opacity="0.38" filter={`url(#${bristle})`} />
        </g>
        {/* flicked powder specks */}
        <g filter={`url(#${rough})`}>
          {specks.map((s, i) => (
            <circle key={i} cx={s.x} cy={s.y} r={s.r} fill={s.c} opacity={s.o} />
          ))}
        </g>
      </g>
    </svg>
  );
};
